import { PAID_LUNCH_MINUTES, DEFAULT_SHIFT_CLOCK_MINUTES } from './schedulingConfig';
import type { DayShiftEntry } from './types';
import { calculateShiftTotalMinutes, timeToMinutes } from './weekly/weeklyUtils';
import { MAX_SHIFT_WORKED_MINUTES } from '../../services/groupFoundation';

export function minutesToTime(totalMinutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, totalMinutes));
  const hours = Math.floor(clamped / 60);
  const mins = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/** End time for a default 8.5h paid shift (9h clock with standard lunch). */
export function defaultEndFromStart(start: string): string {
  return minutesToTime(timeToMinutes(start) + DEFAULT_SHIFT_CLOCK_MINUTES);
}

/** Start time for a default 8.5h paid shift ending at `end`. */
export function defaultStartFromEnd(end: string): string {
  return minutesToTime(timeToMinutes(end) - DEFAULT_SHIFT_CLOCK_MINUTES);
}

export function shiftTimeSignature(
  entry: Pick<DayShiftEntry, 'start' | 'end' | 'lunchStart' | 'lunchEnd'>
): string {
  return `${entry.start}|${entry.end}|${entry.lunchStart}|${entry.lunchEnd}`;
}

/** Pull end earlier so paid worked time ≤ max (keeps lunch window; 0.5h lunch stays paid). */
export function clampShiftToMaxHours(
  entry: DayShiftEntry,
  maxMinutes = MAX_SHIFT_WORKED_MINUTES
): DayShiftEntry {
  const worked = calculateShiftTotalMinutes(entry);
  if (worked <= maxMinutes) return entry;

  const lunchRaw = timeToMinutes(entry.lunchEnd) - timeToMinutes(entry.lunchStart);
  const lunch = Number.isFinite(lunchRaw) ? Math.max(0, lunchRaw) : 0;
  const unpaidLunch = Math.max(0, lunch - PAID_LUNCH_MINUTES);
  const start = timeToMinutes(entry.start);
  return {
    ...entry,
    end: minutesToTime(start + maxMinutes + unpaidLunch),
  };
}

export { calculateShiftTotalMinutes, timeToMinutes };
